import { ItemFile, Order, OrderItem } from "@/types";
import { http, httpForm } from "../utils/http-common";
import { replaceItemFile } from "../services/OrderItems.service";
import { resizeImage } from "../utils/image.utils";
import { setErrorMessage } from "../utils/ToastMessageUtils";

/** Fields accepted by CreateOrderItemDto — excludes client-only data like customFieldValues and synced custom column keys. */
const ORDER_ITEM_API_KEYS = [
  "id",
  "createdAt",
  "updatedAt",
  "metadata",
  "order",
  "active",
  "orderId",
  "currentFile",
  "currentFileId",
  "description",
  "line",
  "parent",
  "quantity",
  "status",
  "lengthUnits",
  "widthUnits",
  "width",
  "length",
  "comments",
  "material",
  "materialId",
  "originalName",
  "workflowStageId",
  "workflowStage",
  "position",
  "parentId",
  "children",
  "receivers",
  "companyId",
  "filesHistory",
  "isNew",
  "isChangingFile",
  "file",
  "orderType",
  "orderTypeId",
  "isAttachment",
  "printer",
  "printerId",
  "scheme",
  "schemeId",
  "action",
  "layout",
  "layoutId",
  "batch",
  "batchId",
  "jobFiles",
  "company",
  "hasPrintJobs",
] as const;

/** Strips client-only keys (customFieldValues, synced custom column values) before order item API calls. */
export function sanitizeOrderItemForApi(item: OrderItem, line: number) {
  const source = item as Record<string, unknown>;
  const clean: Record<string, unknown> = { line };
  for (const key of ORDER_ITEM_API_KEYS) {
    if (key === "line") continue;
    if (source[key] !== undefined) clean[key] = source[key];
  }
  if (typeof clean.id === "number" && clean.id < 0) delete clean.id;
  return clean;
}

export function getOrders() {
  return http
    .get("/orders")
    .then((res) => {
      return res.data.data;
    })
    .catch((err) => {
      console.log(err);
    });
}

export async function getOrder(orderId: number) {
  const res = await http.get(`/orders/${orderId}?with=createdBy,orderItems.customFieldValues,customFieldValues`);
  const order: Order = res.data.data;
  order.orderItems = order.orderItems.map((item: OrderItem) => {
    return { ...item, quantity: Number(item.quantity), width: Number(item.width), length: Number(item.length) };
  });
  return order;
}

export async function getItemsFromOrder(orderId: number) {
  const order = await getOrder(orderId);
  return order.orderItems;
}

export async function sendOrderToPartner(orderId: number, receiverId: string) {
  return http.get(`/orders/${orderId}/send/${receiverId}`);
}
export async function acceptOrder(itemId: number | undefined, workflowId: number) {
  if (!itemId) setErrorMessage("Error accepting order");
  return http.put(`/orderItems/${itemId}/accept`, { workflow: workflowId });
}

export async function deleteOrder(orderId: number): Promise<void> {
  return http.delete(`/orders/${orderId}`);
}
export async function createOrder(order: Order) {
  const payload: any = { ...order };
  delete payload.customFieldValues;
  if (payload.orderItems && payload.orderItems.length > 0) {
    // Assign lines on the live items so post-save matching works; send only API-allowed fields.
    payload.orderItems = order.orderItems.map((item, index) => {
      item.line = index + 1;
      return sanitizeOrderItemForApi(item, index + 1);
    });
  }
  return http.post(`/orders?withfileurls`, payload).then((res) => {
    return res.data.data as Order;
  });
}

export async function createOrderWithoutItems(order: Omit<Order, "orderItems">) {
  return http.post(`/orders`, order).then((res) => {
    return res.data.data as Order;
  });
}
export async function createOrderWithFiles(order: Order, onProgress: any): Promise<Order> {
  const formData = new FormData();
  formData.append("order", JSON.stringify(order));
  for (const item of order.orderItems) {
    if (item.isNew) {
      formData.append("file", item.uploadedFile);
      const previewSrc = await resizeImage(250, item.uploadedFile, 1);
      formData.append("preview", previewSrc, item.uploadedFile.name);
    }
  }

  const config = {
    onUploadProgress: function (progressEvent: any) {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      console.log(percentCompleted);
      onProgress(percentCompleted);
    },
  };
  return httpForm.postForm(`/orders/withFiles`, formData, config).then((res) => {
    return res.data.data as Order;
  });
}
export async function editOrderWithFiles(order: Order, onProgress: any): Promise<Order> {
  const id = order.id;
  delete order.id;

  const formData = new FormData();
  formData.append("order", JSON.stringify(order));
  order.orderItems.forEach((item) => {
    if (item.isNew) formData.append("file", item.uploadedFile);
    if (item.isChangingFile) replaceItemFile(item, onProgress);
  });

  const config = {
    onUploadProgress: function (progressEvent: any) {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      console.log(percentCompleted);
      onProgress(percentCompleted);
    },
  };
  return httpForm.putForm(`/orders/withFiles/${id}`, formData, config).then((res) => {
    return res.data.data as Order;
  });
}

export async function editOrder(order: Order | Pick<Order, "workflowStageId"> | any, id: number): Promise<Order> {
  const orderToSend: any = { ...order };
  delete orderToSend.ownerId;
  delete orderToSend.rootComment;
  delete orderToSend.customFieldValues;
  //if (orderToSend.dueDate) orderToSend.dueDate = dayjs(orderToSend.dueDate).format("YYYY-MM-DD HH:mm:ss.SSS ZZ");
  if (orderToSend.orderItems && orderToSend.orderItems.length > 0) {
    orderToSend.orderItems = orderToSend.orderItems.map((item: OrderItem, index: number) => {
      if (!item.line) item.line = index + 1;
      return sanitizeOrderItemForApi(item, item.line ?? index + 1);
    });
  }
  return http.put(`/orders/${id}?withfileurls`, orderToSend).then((res) => res.data.data);
}

export async function downloadOrderItemFile(file: ItemFile | undefined): Promise<void> {
  if (!file) {
    throw new Error("File not found");
  }

  let url = "";
  let filename = file.originalName || "download";
  if (file.location === "URL" && file.path) {
    url = file.path;
  } else if (file.id) {
    const response = await http.get(`/files/download/${file.id}`);
    url = response.data.url;
    filename = response.data.originalName || filename;
  } else {
    throw new Error("File ID or Path missing");
  }

  if (!url) {
    throw new Error("Download URL not found");
  }

  try {
    // Kick off the real, native browser download (keeps the browser's own
    // progress UI and Save dialog). This request triggers the slow server-side
    // file generation.
    triggerNativeDownload(url, filename);

    // The browser fires no JS event when a native download starts, and this is a
    // cross-origin generation service we don't control (so we can't read its
    // response or have it set a cookie). We fire a parallel `no-cors` probe whose
    // promise resolves once the service sends its response headers, i.e. when
    // generation has finished and the download is starting. Awaiting it lets the
    // caller keep a loading state until then.
    // NOTE: this hits the generation service a second time; if it doesn't cache
    // the result, the file may be generated twice.
    await waitForDownloadToStart(url);
  } catch (error) {
    console.error("Download failed: ", error);
    throw error;
  }
}

function triggerNativeDownload(url: string, filename: string): void {
  const iframeName = "order-item-file-download";
  let iframe = document.querySelector<HTMLIFrameElement>(`iframe[name="${iframeName}"]`);

  if (!iframe) {
    iframe = document.createElement("iframe");
    iframe.name = iframeName;
    iframe.style.display = "none";
    document.body.appendChild(iframe);
  }

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.target = iframeName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Resolves once the (cross-origin) URL responds, signalling that generation is
// done and the download is starting. Falls back to a timeout so the loading
// state can never hang indefinitely if the probe is blocked.
async function waitForDownloadToStart(url: string, timeoutMs = 120000): Promise<void> {
  const probe = fetch(url, { mode: "no-cors", credentials: "omit", cache: "no-store" }).catch(() => undefined);
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));
  await Promise.race([probe, timeout]);
}

export async function downloadPrintJobLog(log: string | undefined, orderIdentifier: string): Promise<void> {
  if (!log) {
    throw new Error("Log data not found");
  }

  try {
    let formattedLog = log;
    try {
      formattedLog = JSON.stringify(JSON.parse(log), null, 2);
    } catch {
      console.warn("Log is not valid JSON, downloading as plain text");
    }

    const blob = new Blob([formattedLog], { type: "application/json" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `job-log-${orderIdentifier}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download failed: ", error);
    throw error;
  }
}

export async function getChildOrders(orderId: number) {
  return http.get(`/orders/${orderId}/children`).then((res) => res.data.data);
}

export async function rejectOrder(orderId: number, reason: string) {
  return http.put(`/orders/${orderId}/reject`, { reason: reason });
}

// function downloadWithBlob(blob: Blob, name: string) {
//   const link = document.createElement("a");
//   link.href = URL.createObjectURL(blob);
//   link.download = name;
//   link.click();
//   URL.revokeObjectURL(link.href);
//   link.remove();
// }

export async function downloadOrderPDF(orderId: number, orderCode: string) {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await http.get(`/orders/${orderId}/downloadPDF?timezone=${timezone}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${orderCode}.pdf`); // set the filename

    document.body.appendChild(link);
    link.click();
  } catch (error) {
    console.error(error);
  }
}
